import { useCallback } from 'react';
import { useRouter, useRouteContext } from '@tanstack/react-router';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import { signOutUser } from '@/server/auth';
import type { Profile } from '@/apiTypes/profile';

interface AuthContextValue {
  profile: Profile | null;
  signOut: () => Promise<void>;
}

interface AuthActions {
  signOut: () => Promise<void>;
  invalidate: () => Promise<void>;
}

/**
 * Hook to access authentication state.
 * Reads profile from TanStack Router route context (populated by root beforeLoad).
 * Must be used within a routed component.
 */
export function useAuth(): AuthContextValue {
  const profile = useRouteContext({
    from: '__root__',
    select: (ctx) => ctx.profile,
  });
  const { signOut } = useAuthActions();

  return { profile, signOut };
}

/**
 * Hook providing auth actions (sign out, invalidate).
 * `invalidate` re-runs the root beforeLoad to refresh auth state from cookies.
 * Use after any operation that changes auth state (profile update, login, etc.).
 */
export function useAuthActions(): AuthActions {
  const router = useRouter();

  const signOut = useCallback(async () => {
    await signOutUser();
    const supabase = getSupabaseBrowserClient();
    await supabase.auth.signOut();
    await router.invalidate();
  }, [router]);

  const invalidate = useCallback(
    () => router.invalidate(),
    [router],
  );

  return { signOut, invalidate };
}
